/**
 * jiraItemSync — an item linked to a Jira issue, and what comes over.
 *
 * The link is the item's entity_external_ids row for Jira (the issue key, such
 * as SCRUM-1, is the join key — what ItemJiraSyncPanel registers). Syncing reads
 * the issue through the integrations engine (`/jira/estimates`, which finds the
 * site's story-points field by name) and runs it through the workspace's Jira
 * import mapping set with the shared routine (linkedItemSync).
 *
 * Until the workspace maps otherwise, the set that runs is "Jira item sync":
 * story points into Effort, on Effort's "Story Points" scale when the workspace
 * has one — saved into the Jira connector's field mappings the first time, so
 * Settings → Field Mappings shows and governs it from then on. Jira's broader
 * default set (title, description, category, status) is an import/export
 * template and is never applied to an item someone links by hand.
 */

import { useAttributesStore } from '../stores/prioritization/attributesStore';
import {
  loadFieldMappingConfig,
  saveFieldMappingConfig,
  type FieldMappingConfig,
  type FieldMappingSet,
} from './fieldMappingRegistry';
import { resolveActiveMappingSet } from './fieldMappingApply';
import { readJiraEstimates, type JiraIssueEstimateRead } from './semanticConnectors/jiraSemanticApi';
import { applyLinkedValuesToItem, describeLinkedSync, type LinkedSyncResult } from './linkedItemSync';

export const JIRA_CONNECTOR = 'jira';

/** An issue keyed the way CONNECTOR_FIELDS.jira names its fields, so the workspace's Jira rules read it. */
export function jiraIssueExternalValues(issue: JiraIssueEstimateRead): Record<string, unknown> {
  return {
    issue_key: issue.key,
    summary: issue.summary,
    issue_type: issue.issueType ?? '',
    status: issue.status ?? '',
    story_points: issue.storyPoints ?? '',
  };
}

/** The Effort scale story points belong on: the one named "Story Points" (any case). */
export function storyPointsScaleId(
  scales: ReadonlyArray<{ id?: string; label?: string }> | undefined,
): string | undefined {
  return scales?.find((s) => /^story\s*points?$/i.test((s.label ?? '').trim()))?.id;
}

export function getDefaultJiraItemSyncSet(effortScaleId?: string): FieldMappingSet {
  const now = new Date().toISOString();
  return {
    id: 'jira-item-sync',
    connectorType: JIRA_CONNECTOR,
    name: 'Jira item sync',
    description: 'What a linked Jira issue brings over: its story points, into Effort.',
    createdAt: now,
    updatedAt: now,
    entries: [
      {
        id: 'jira-item-sync-story-points',
        externalFieldKey: 'story_points',
        internalFieldKey: 'attribute:effort',
        direction: 'import',
        enabled: true,
        valueMap: [],
        ...(effortScaleId ? { scaleId: effortScaleId } : {}),
      },
    ],
  };
}

/** The import set that applies to a linked Jira issue; the item-sync default is saved and picked when there is none. */
export async function ensureJiraItemMappingSet(
  workspaceId: string,
): Promise<{ set: FieldMappingSet; created: boolean }> {
  const config = await loadFieldMappingConfig(workspaceId, JIRA_CONNECTOR);
  const active = resolveActiveMappingSet(config, 'import');
  if (active.set) return { set: active.set, created: false };
  const store = useAttributesStore.getState();
  if (store.attributes.length === 0) await store.fetchFromSupabase();
  const effort = useAttributesStore.getState().attributes.find((a) => a.key === 'effort');
  const set = getDefaultJiraItemSyncSet(storyPointsScaleId(effort?.scales));
  const next: FieldMappingConfig = {
    ...config,
    version: 3,
    sets: [...config.sets.filter((s) => s.id !== set.id), set],
    importSetId: set.id,
  };
  const { error } = await saveFieldMappingConfig(workspaceId, JIRA_CONNECTOR, next);
  if (error) throw new Error(`The Jira mapping set could not be saved: ${error}`);
  return { set, created: true };
}

export interface JiraSyncResult extends LinkedSyncResult {
  setCreated: boolean;
}

/** Re-read a linked Jira issue and bring its mapped fields onto the item. */
export async function syncLinkedJiraIssue(params: {
  workspaceId: string;
  itemId: string;
  issueKey: string;
  issueUrl?: string | null;
  userId?: string | null;
  reason: 'link' | 'now' | 'auto';
}): Promise<JiraSyncResult> {
  const { workspaceId, itemId, issueKey, userId, reason } = params;
  const fail = (message: string): JiraSyncResult => ({ ok: false, wrote: [], kept: [], skipped: [], errors: [message], setCreated: false });

  let issue: JiraIssueEstimateRead | undefined;
  try {
    issue = (await readJiraEstimates({ workspaceId, keys: [issueKey] })).find((i) => i.key === issueKey);
  } catch (err) {
    return fail(err instanceof Error ? err.message : 'Jira could not be read');
  }
  if (!issue) return fail(`Jira has no issue ${issueKey} this connection can see`);

  let set: FieldMappingSet;
  let setCreated = false;
  try {
    const ensured = await ensureJiraItemMappingSet(workspaceId);
    set = ensured.set;
    setCreated = ensured.created;
  } catch (err) {
    return fail(err instanceof Error ? err.message : 'The Jira mapping set could not be read');
  }

  const linked = await applyLinkedValuesToItem({
    connectorType: JIRA_CONNECTOR,
    systemName: 'Jira',
    workspaceId,
    itemId,
    userId,
    reason,
    record: {
      id: issue.key,
      label: issue.key,
      url: params.issueUrl ?? '',
      title: issue.summary,
      updatedAt: new Date().toISOString(),
      projectKey: issue.key.split('-')[0],
    },
    values: jiraIssueExternalValues(issue),
    set,
  });
  return { ...linked, setCreated };
}

export function describeJiraSync(result: JiraSyncResult): string {
  return describeLinkedSync(result, 'the Jira issue');
}
